import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Close as CloseIcon,
  PersonOutline,
  Check as CheckIcon,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { specimenService } from "../../services/lis";

export default function CollectSampleModal({
  open,
  orderId,
  isDark,
  onClose,
  onCollected,
}) {
  const { user } = useAuth();

  // Theme tokens
  const cardBg = isDark ? "#1E1E1E" : "#FFFFFF";
  const innerCardBg = isDark ? "#141414" : "#F8FAFC";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0";
  const textPrimary = isDark ? "#F8FAFC" : "#0F172A";
  const textSecondary = isDark ? "#94A3B8" : "#64748B";

  const [loading, setLoading] = useState(false);
  const [collecting, setCollecting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  // Data states
  const [orderInfo, setOrderInfo] = useState({
    patientName: "—",
    patientId: "—",
    orderNumber: "—",
    orderDate: "—",
    testsOrdered: [],
  });
  const [samples, setSamples] = useState([]);
  const loggedInName = user?.firstName
    ? `${user.firstName} ${user.lastName || ""}`.trim()
    : user?.name || user?.email || "Laboratory Staff";

  const [collectorName, setCollectorName] = useState(loggedInName);
  const [notes, setNotes] = useState("");

  // Body site options for tissue/biopsy specimens
  const BODY_SITES = [
    'Brain', 'Head/Neck', 'Thyroid', 'Chest', 'Breast', 'Lung',
    'Heart', 'Abdomen', 'Liver', 'Kidney', 'Pancreas', 'Spleen',
    'Stomach', 'Small Intestine', 'Large Intestine', 'Rectum',
    'Spine', 'Pelvis', 'Prostate', 'Uterus', 'Cervix', 'Ovary',
    'Testis', 'Bladder', 'Lymph Node', 'Bone', 'Bone Marrow',
    'Skin', 'Soft Tissue', 'Eye', 'Oral Cavity', 'Other'
  ];
  const LATERALITY_OPTIONS = ['Left', 'Right', 'Bilateral', 'Midline', 'N/A'];
  const TISSUE_TYPES = ['Tissue', 'Aspirate', 'Bone Marrow'];

  const isTissueSpecimen = (sampleType) =>
    TISSUE_TYPES.some(t => (sampleType || '').toLowerCase().includes(t.toLowerCase()));

  const handleBodySiteChange = (index, field, value) => {
    setSamples((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  // Fetch grouping from backend if orderId is provided
  const fetchPreview = useCallback(async () => {
    if (!orderId) {
      setOrderInfo({
        patientName: "—",
        patientId: "—",
        orderNumber: "—",
        orderDate: "—",
        testsOrdered: [],
      });
      setSamples([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await specimenService.previewCollection(orderId);
      if (res?.data?.data) {
        const data = res.data.data;
        const ord = data.order || data.orderInfo || {};
        setOrderInfo({
          patientName:
            ord.patientName ||
            (ord.patientId?.firstName
              ? `${ord.patientId.firstName} ${ord.patientId.lastName || ""}`
              : "Patient"),
          patientId: ord.patientId?.patientId || ord.patientId?.mrn || (typeof ord.patientId === 'string' ? ord.patientId : "—"),
          orderNumber: ord.orderNumber || ord.orderId || "—",
          orderDate: ord.orderDate || ord.createdAt
            ? new Date(ord.orderDate || ord.createdAt).toLocaleString("en-US", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "—",
          testsOrdered:
            data.testsOrdered ||
            ord.tests?.map((t) => t.displayName || t.testName) || [],
        });

        const grps = data.specimenGroups || data.groups || [];
        if (grps && grps.length > 0) {
          setSamples(
            grps.map((g, idx) => ({
              sampleType: g.specimenType || "Blood",
              containerType: g.containerType || "",
              containerColor: g.containerColor || "",
              orderItemIds: g.orderItemIds || [],
              testsUsingSample:
                g.testNames ||
                (g.tests || []).map((t) => t.displayName || t.testName).join(", ") ||
                "Laboratory Test",
              sampleId:
                g.suggestedSampleId ||
                g.sampleId ||
                `SMP-${String(100 + idx).padStart(5, "0")}`,
            }))
          );
        } else {
          setSamples([]);
        }
      } else {
        setSamples([]);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load sample grouping");
      setSamples([]);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (open) {
      setAttemptedSubmit(false);
      setError("");
      setSuccess("");
      fetchPreview();
    }
  }, [open, fetchPreview]);

  // Update sample ID on user edit
  const handleSampleIdChange = (index, val) => {
    setSamples((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], sampleId: val };
      return next;
    });
  };

  // Confirm Collection
  const handleConfirmCollection = async () => {
    setError("");
    setSuccess("");
    setAttemptedSubmit(true);

    const missingSampleId = samples.find((s) => !s.sampleId?.trim());
    if (missingSampleId) {
      setError("Please ensure all samples have a valid Sample ID.");
      return;
    }

    // Compulsory check: Anatomical Body Site is strictly required for tissue / biopsy / aspirate / bone marrow specimens
    const missingBodySite = samples.find(
      (s) => isTissueSpecimen(s.sampleType) && !s.bodySite?.trim()
    );
    if (missingBodySite) {
      setError(
        `Anatomical Body Site is compulsory for "${missingBodySite.sampleType}" specimens. Please select or enter the body site.`
      );
      return;
    }

    setCollecting(true);
    try {
      if (orderId) {
        await specimenService.collectSpecimens({
          orderId,
          specimens: samples.map((s) => ({
            specimenType: s.sampleType,
            sampleId: s.sampleId.trim(),
            containerType: s.containerType || "",
            containerColor: s.containerColor || "",
            orderItemIds: s.orderItemIds || [],
            collectorNotes: notes,
            ...(isTissueSpecimen(s.sampleType) && {
              bodySite: s.bodySite || '',
              laterality: s.laterality || '',
              bodySiteDetail: s.bodySiteDetail || '',
            }),
          })),
        });
      }
      setSuccess("Samples marked as collected successfully!");
      setTimeout(() => {
        onCollected?.();
        onClose();
      }, 1000);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to confirm sample collection");
    } finally {
      setCollecting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: "95vw", sm: "92vw", md: "860px", lg: "900px" },
          maxWidth: "920px !important",
          maxHeight: "88vh",
          bgcolor: cardBg,
          borderRadius: "14px",
          border: `1px solid ${borderColor}`,
          boxShadow: isDark
            ? "0 8px 32px rgba(0,0,0,0.5)"
            : "0 8px 32px rgba(15, 23, 42, 0.08)",
          p: 0,
        },
      }}
    >
      {/* 1. Modal Header with Close Button */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2.5,
          py: 1.25,
          borderBottom: `1px solid ${borderColor}`,
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Satoshi', sans-serif",
            fontSize: "1.1rem",
            fontWeight: 800,
            color: textPrimary,
            letterSpacing: "-0.01em",
          }}
        >
          Collect Sample(s)
        </Typography>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: textSecondary,
            "&:hover": {
              bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
              color: textPrimary,
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      {/* Modal Content */}
      <DialogContent sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5, py: 0.5, borderRadius: "8px" }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 1.5, py: 0.5, borderRadius: "8px" }}>
            {success}
          </Alert>
        )}

        {/* 2. PATIENT & ORDER INFORMATION CARD */}
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: "10px",
            bgcolor: cardBg,
            border: `1px solid ${borderColor}`,
            mb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography
              sx={{
                fontFamily: "'Satoshi', sans-serif",
                fontSize: "0.75rem",
                fontWeight: 800,
                color: "#2563EB",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              PATIENT & ORDER INFORMATION
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Satoshi', sans-serif",
                fontSize: "0.78rem",
                fontWeight: 700,
                color: textSecondary,
              }}
            >
              Tests Ordered ({orderInfo.testsOrdered.length})
            </Typography>
          </Box>

          <Grid container spacing={1.5} alignItems="center">
            {/* 4 Key details in 2x2 grid */}
            <Grid item xs={12} sm={7}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: textSecondary, fontSize: "0.72rem", display: "block" }}>
                    Patient Name
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Satoshi', sans-serif",
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      color: textPrimary,
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {orderInfo.patientName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: textSecondary, fontSize: "0.72rem", display: "block" }}>
                    Patient ID
                  </Typography>
                  <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", fontWeight: 600, color: textPrimary }}>
                    {orderInfo.patientId}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: textSecondary, fontSize: "0.72rem", display: "block" }}>
                    Order ID
                  </Typography>
                  <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", fontWeight: 700, color: textPrimary }}>
                    {orderInfo.orderNumber}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: textSecondary, fontSize: "0.72rem", display: "block" }}>
                    Order Date
                  </Typography>
                  <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", fontWeight: 600, color: textPrimary }}>
                    {orderInfo.orderDate}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            {/* Vertical Divider */}
            <Divider
              orientation="vertical"
              flexItem
              sx={{ display: { xs: "none", sm: "block" }, mx: 0.5, borderColor }}
            />

            {/* Right Column: Tests Ordered as compact chips */}
            <Grid item xs={12} sm>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, maxHeight: 68, overflowY: "auto" }}>
                {orderInfo.testsOrdered.length > 0 ? (
                  orderInfo.testsOrdered.map((test, i) => (
                    <Box
                      key={i}
                      sx={{
                        px: 1,
                        py: 0.3,
                        borderRadius: "6px",
                        bgcolor: isDark ? "rgba(37,99,235,0.12)" : "#EFF6FF",
                        color: "#2563EB",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        fontFamily: "'Satoshi', sans-serif",
                        border: `1px solid ${isDark ? "rgba(37,99,235,0.25)" : "#BFDBFE"}`,
                      }}
                    >
                      {test}
                    </Box>
                  ))
                ) : (
                  <Typography sx={{ fontSize: "0.8rem", color: textSecondary }}>None listed</Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* 3. SAMPLES REQUIRED CARD */}
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: "10px",
            bgcolor: cardBg,
            border: `1px solid ${borderColor}`,
            mb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography
              sx={{
                fontFamily: "'Satoshi', sans-serif",
                fontSize: "0.75rem",
                fontWeight: 800,
                color: "#2563EB",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              SAMPLES REQUIRED ({samples.length})
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Satoshi', sans-serif",
                fontSize: "0.75rem",
                color: textSecondary,
              }}
            >
              Enter Sample ID and anatomical details where required
            </Typography>
          </Box>

          <TableContainer
            sx={{
              borderRadius: "8px",
              border: `1px solid ${borderColor}`,
              overflow: "hidden",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: isDark ? "#171717" : "#F8FAFC" }}>
                  <TableCell
                    sx={{
                      fontFamily: "'Satoshi', sans-serif",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      color: textSecondary,
                      py: 0.8,
                      px: 1.5,
                      borderBottom: `1px solid ${borderColor}`,
                      width: "25%",
                    }}
                  >
                    Specimen Type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "'Satoshi', sans-serif",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      color: textSecondary,
                      py: 0.8,
                      px: 1.5,
                      borderBottom: `1px solid ${borderColor}`,
                      width: "45%",
                    }}
                  >
                    Tests Using This Specimen
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "'Satoshi', sans-serif",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      color: textSecondary,
                      py: 0.8,
                      px: 1.5,
                      borderBottom: `1px solid ${borderColor}`,
                      width: "30%",
                    }}
                  >
                    Sample ID
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {samples.map((sample, index) => (
                  <React.Fragment key={index}>
                    <TableRow
                      sx={{
                        "&:last-child td": { borderBottom: isTissueSpecimen(sample.sampleType) ? `1px solid ${borderColor}` : 0 },
                        "&:hover": {
                          bgcolor: isDark
                            ? "rgba(255,255,255,0.02)"
                            : "rgba(241,245,249,0.5)",
                        },
                      }}
                    >
                      {/* Sample Type */}
                      <TableCell
                        sx={{
                          fontFamily: "'Satoshi', sans-serif",
                          fontWeight: 600,
                          fontSize: "0.8rem",
                          color: textPrimary,
                          px: 1.5,
                          py: 0.8,
                          borderBottom: `1px solid ${borderColor}`,
                        }}
                      >
                        {sample.sampleType}
                        {isTissueSpecimen(sample.sampleType) && (
                          <Typography
                            component="span"
                            sx={{
                              ml: 1,
                              fontSize: "0.65rem",
                              fontFamily: "'Satoshi', sans-serif",
                              fontWeight: 700,
                              color: "#E67E22",
                              bgcolor: isDark ? "rgba(230,126,34,0.15)" : "rgba(230,126,34,0.1)",
                              px: 0.8,
                              py: 0.2,
                              borderRadius: "4px",
                            }}
                          >
                            TISSUE
                          </Typography>
                        )}
                      </TableCell>

                      {/* Tests Using This Sample */}
                      <TableCell
                        sx={{
                          fontFamily: "'Satoshi', sans-serif",
                          fontSize: "0.8rem",
                          color: textPrimary,
                          px: 1.5,
                          py: 0.8,
                          borderBottom: `1px solid ${borderColor}`,
                        }}
                      >
                        {sample.testsUsingSample}
                      </TableCell>

                      {/* Editable Sample ID Input */}
                      <TableCell
                        sx={{
                          px: 1.5,
                          py: 0.6,
                          borderBottom: `1px solid ${borderColor}`,
                        }}
                      >
                        <TextField
                          size="small"
                          required
                          value={sample.sampleId}
                          error={attemptedSubmit && !sample.sampleId?.trim()}
                          helperText={attemptedSubmit && !sample.sampleId?.trim() ? "Required" : ""}
                          onChange={(e) =>
                            handleSampleIdChange(index, e.target.value)
                          }
                          sx={{
                            width: "100%",
                            maxWidth: 180,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "6px",
                              fontFamily: "'Satoshi', sans-serif",
                              fontSize: "0.8rem",
                              fontWeight: 600,
                              height: 32,
                              bgcolor: isDark ? "#141414" : "#FFFFFF",
                              "& fieldset": { borderColor: attemptedSubmit && !sample.sampleId?.trim() ? "#EF4444" : borderColor },
                              "&:hover fieldset": { borderColor: "#3B82F6" },
                              "&.Mui-focused fieldset": { borderColor: "#2563EB" },
                            },
                          }}
                        />
                      </TableCell>
                    </TableRow>

                    {/* Body Site Row — only for tissue/aspirate/bone marrow specimens */}
                    {isTissueSpecimen(sample.sampleType) && (
                      <TableRow
                        sx={{
                          bgcolor: attemptedSubmit && !sample.bodySite?.trim()
                            ? (isDark ? "rgba(239, 68, 68, 0.08)" : "#FEF2F2")
                            : (isDark ? "rgba(230,126,34,0.04)" : "rgba(230,126,34,0.03)"),
                        }}
                      >
                        <TableCell colSpan={3} sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${borderColor}` }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.8 }}>
                            <Typography
                              sx={{
                                fontFamily: "'Satoshi', sans-serif",
                                fontSize: "0.72rem",
                                fontWeight: 800,
                                color: attemptedSubmit && !sample.bodySite?.trim() ? "#DC2626" : "#E67E22",
                                textTransform: "uppercase",
                                letterSpacing: "0.03em",
                              }}
                            >
                              Anatomical Site Details *
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.65rem",
                                fontFamily: "'Satoshi', sans-serif",
                                fontWeight: 800,
                                color: attemptedSubmit && !sample.bodySite?.trim() ? "#EF4444" : "#E67E22",
                                bgcolor: attemptedSubmit && !sample.bodySite?.trim() ? "rgba(239, 68, 68, 0.15)" : (isDark ? "rgba(230,126,34,0.15)" : "rgba(230,126,34,0.1)"),
                                px: 0.8,
                                py: 0.2,
                                borderRadius: "4px",
                              }}
                            >
                              {attemptedSubmit && !sample.bodySite?.trim() ? "⚠️ BODY SITE REQUIRED" : "REQUIRED *"}
                            </Typography>
                          </Box>
                          <Grid container spacing={1.2}>
                            <Grid item xs={12} sm={4}>
                              <Autocomplete
                                freeSolo
                                options={BODY_SITES}
                                value={sample.bodySite || ""}
                                onChange={(_, val) => handleBodySiteChange(index, "bodySite", val || "")}
                                onInputChange={(_, val) => handleBodySiteChange(index, "bodySite", val || "")}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Body Site *"
                                    required
                                    error={attemptedSubmit && !sample.bodySite?.trim()}
                                    helperText={attemptedSubmit && !sample.bodySite?.trim() ? "Body site is required" : ""}
                                    size="small"
                                    placeholder="e.g., Breast, Skin, Cervix"
                                    InputProps={{ ...params.InputProps, sx: { fontFamily: "'Satoshi', sans-serif", fontSize: "0.78rem", height: 32 } }}
                                    InputLabelProps={{ sx: { fontFamily: "'Satoshi', sans-serif", fontSize: "0.78rem", fontWeight: 600 } }}
                                  />
                                )}
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.78rem" }}>Laterality</InputLabel>
                                <Select
                                  value={sample.laterality || ""}
                                  onChange={(e) => handleBodySiteChange(index, "laterality", e.target.value)}
                                  label="Laterality"
                                  sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.78rem", height: 32 }}
                                >
                                  <MenuItem value=""><em>—</em></MenuItem>
                                  {LATERALITY_OPTIONS.map(opt => (
                                    <MenuItem key={opt} value={opt} sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.8rem" }}>{opt}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                              <TextField
                                label="Site Detail (optional)"
                                size="small"
                                fullWidth
                                value={sample.bodySiteDetail || ""}
                                onChange={(e) => handleBodySiteChange(index, "bodySiteDetail", e.target.value)}
                                placeholder="e.g., Upper outer quadrant"
                                InputProps={{ sx: { fontFamily: "'Satoshi', sans-serif", fontSize: "0.78rem", height: 32 } }}
                                InputLabelProps={{ sx: { fontFamily: "'Satoshi', sans-serif", fontSize: "0.78rem" } }}
                              />
                            </Grid>
                          </Grid>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* 4. ADDITIONAL INFORMATION CARD */}
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: "10px",
            bgcolor: cardBg,
            border: `1px solid ${borderColor}`,
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontSize: "0.75rem",
              fontWeight: 800,
              color: "#2563EB",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              mb: 1.2,
            }}
          >
            ADDITIONAL INFORMATION
          </Typography>

          <Grid container spacing={2} alignItems="center">
            {/* Collected By */}
            <Grid item xs={12} sm={4}>
              <Typography
                sx={{
                  fontFamily: "'Satoshi', sans-serif",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  color: textPrimary,
                  mb: 0.5,
                }}
              >
                Collected By
              </Typography>

              <TextField
                fullWidth
                size="small"
                value={collectorName}
                onChange={(e) => setCollectorName(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <PersonOutline sx={{ color: textSecondary, fontSize: 17, mr: 0.8 }} />
                  ),
                  sx: { height: 34, fontSize: "0.82rem", fontFamily: "'Satoshi', sans-serif" }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    fontWeight: 600,
                    bgcolor: isDark ? "#141414" : "#F8FAFC",
                    "& fieldset": { borderColor },
                  },
                }}
              />
            </Grid>

            {/* Notes (Optional) */}
            <Grid item xs={12} sm={8}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography
                  sx={{
                    fontFamily: "'Satoshi', sans-serif",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    color: textPrimary,
                  }}
                >
                  Notes <Typography component="span" sx={{ fontSize: "0.75rem", color: textSecondary, fontWeight: 400 }}>(Optional)</Typography>
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: textSecondary }}>
                  {notes.length} / 250
                </Typography>
              </Box>

              <TextField
                fullWidth
                size="small"
                placeholder="Enter any collection notes..."
                value={notes}
                onChange={(e) => {
                  if (e.target.value.length <= 250) setNotes(e.target.value);
                }}
                InputProps={{ sx: { height: 34, fontSize: "0.82rem", fontFamily: "'Satoshi', sans-serif" } }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    bgcolor: isDark ? "#141414" : "#FFFFFF",
                    "& fieldset": { borderColor },
                    "&:hover fieldset": { borderColor: "#3B82F6" },
                  },
                }}
              />
            </Grid>
          </Grid>
        </Paper>
      </DialogContent>

      {/* 5. Modal Footer Actions */}
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.25,
          borderTop: `1px solid ${borderColor}`,
          gap: 1.5,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderRadius: "8px",
            borderColor: borderColor,
            color: textSecondary,
            textTransform: "none",
            fontFamily: "'Satoshi', sans-serif",
            fontWeight: 600,
            fontSize: "0.82rem",
            px: 2.5,
            py: 0.65,
            "&:hover": {
              borderColor: "#3B82F6",
              bgcolor: isDark ? "rgba(37,99,235,0.05)" : "#EFF6FF",
            },
          }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleConfirmCollection}
          disabled={collecting}
          startIcon={!collecting && <CheckIcon sx={{ fontSize: 16 }} />}
          sx={{
            borderRadius: "8px",
            bgcolor: "#2563EB",
            color: "#FFFFFF",
            textTransform: "none",
            fontFamily: "'Satoshi', sans-serif",
            fontWeight: 700,
            fontSize: "0.82rem",
            px: 3,
            py: 0.7,
            boxShadow: "0 4px 14px rgba(37, 99, 235, 0.3)",
            "&:hover": {
              bgcolor: "#1D4ED8",
              boxShadow: "0 6px 18px rgba(37, 99, 235, 0.4)",
            },
            "&.Mui-disabled": {
              bgcolor: isDark ? "#2D3748" : "#E2E8F0",
              color: isDark ? "#718096" : "#94A3B8",
            },
          }}
        >
          {collecting ? (
            <CircularProgress size={18} sx={{ color: "#ffffff" }} />
          ) : (
            "Confirm & Mark Collected"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
